import React, { useEffect } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { PlusIcon } from "@components/icons";
import { Select } from "@components/inputs/Select/Select";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  useMarkNewAccountsAsSeenMutation,
  useModifyAccountsMutation,
  useSetAccountAsMainMutation,
} from "@services/budget/mutations";
import { type IBudgetNewAccounts } from "@services/budget/types";
import { NewBudgetBankSection } from "./component/NewBudgetBankSection";

interface INewBudgetAccountsModalProps extends IUseModalProps {
  data: IBudgetNewAccounts;
}

export interface NewBudgetAccountsForm {
  banks: {
    accounts: {
      bankAccountId: number;
      accountName: string;
      isSelected: boolean;
    }[];
  }[];
}

export const NewBudgetAccountsModal: React.FC<INewBudgetAccountsModalProps> = (
  props,
) => {
  const { data, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const [mainSelectedAccount, setMainSelectedAccount] = React.useState<
    number | undefined
  >(undefined);
  const { mutateAsync: setMain, isPending: isSetMainPending } =
    useSetAccountAsMainMutation();
  const { mutateAsync: modifyAccounts, isPending: isModifyAccountsPending } =
    useModifyAccountsMutation();
  const isLoading = isSetMainPending || isModifyAccountsPending;
  const { mutateAsync: markAsSeen } = useMarkNewAccountsAsSeenMutation();

  const { control, setValue, getValues, handleSubmit } =
    useForm<NewBudgetAccountsForm>({
      defaultValues: {
        banks: data.banks.map((bank) => ({
          accounts: bank.bankAccounts.map((account) => ({
            bankAccountId: account.bankAccountId,
            accountName: account.accountName,
            isSelected: account.isSelected,
          })),
        })),
      },
    });

  const mainAccountOptions = data?.banks
    .map((bank) =>
      bank.bankAccounts.map((account) => ({
        value: account.bankAccountId,
        label: `${account.iban} (${bank.bankName})`,
      })),
    )
    .flat();

  useEffect(() => {
    if (!mainSelectedAccount && data) {
      data.banks.forEach((bank) => {
        bank.bankAccounts.forEach((account) => {
          if (account.isMain) {
            setMainSelectedAccount(account.bankAccountId);
          }
        });
      });
    }
  }, [data, mainSelectedAccount]);

  const onSubmit: SubmitHandler<NewBudgetAccountsForm> = async (data) => {
    await markAsSeen({
      bankAccountIds: data.banks
        .map((bank) => bank.accounts.map((account) => account.bankAccountId))
        .flat(),
    });

    await modifyAccounts({
      bankAccounts: data.banks
        .map((bank) => bank.accounts.map((account) => account))
        .flat(),
    });

    if (mainSelectedAccount) {
      await setMain({ bankAccountId: mainSelectedAccount });
    }

    modal.close();
  };

  return (
    <Modal
      modal={modal}
      title={t("budget.newAccountsModal.title")}
      subtitle={t("budget.newAccountsModal.subtitle")}
      badgeProps={{ icon: PlusIcon, color: "green" }}
      actionButtons={
        <React.Fragment>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("budget.newAccountsModal.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
          >
            {t("budget.newAccountsModal.submit")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          value={mainSelectedAccount}
          onChange={(value) => setMainSelectedAccount(value as number)}
          label={t("budget.newAccountsModal.mainAccount")}
          options={mainAccountOptions ?? []}
        />
        {data?.banks.map((bank, index) => (
          <NewBudgetBankSection
            bank={bank}
            bankIndex={index}
            control={control}
            getValues={getValues}
            setValue={setValue}
          />
        ))}
      </div>
    </Modal>
  );
};
